// Shared transfer hub cities, main stations, and point helpers.
// Keep MAIN_STATION_BY_CITY in sync with busScheduleMeta.js / pickupTimes.ts

import { MAIN_STATION_BY_CITY } from './busScheduleMeta';
import { composePlaceLabel } from '../utils/composePlaceLabel';

export const SHARED_TRANSFER_CITIES = ['Nairobi', 'Arusha', 'Moshi'];

/** Directed hub pairs accepted by CreateTripForm (pickup before dropoff on the corridor). */
export const SHARED_TRANSFER_CITY_PAIRS = [
  { pickup: 'Nairobi', dropoff: 'Arusha' },
  { pickup: 'Nairobi', dropoff: 'Moshi' },
  { pickup: 'Arusha', dropoff: 'Moshi' },
  { pickup: 'Arusha', dropoff: 'Nairobi' },
  { pickup: 'Moshi', dropoff: 'Arusha' },
  { pickup: 'Moshi', dropoff: 'Nairobi' },
];

/** Bias centers for city-scoped Places autocomplete (km radii match server validation). */
export const HUB_BIAS_BY_CITY = {
  Nairobi: { lat: -1.286389, lng: 36.817223, radiusKm: 45 },
  Arusha: { lat: -3.386925, lng: 36.682995, radiusKm: 35 },
  Moshi: { lat: -3.3488, lng: 37.3388, radiusKm: 28 },
};

/** Approximate coords for main stations (ops / map bias; validation uses hub zones). */
export const MAIN_STATION_COORDS = {
  Nairobi: { lat: -1.2921, lng: 36.8219 },
  Arusha: { lat: -3.3869, lng: 36.6830 },
  Moshi: { lat: -3.3488, lng: 37.3388 },
};

export function segmentEndpointCities(segment) {
  const m = String(segment || '').match(/^(.+)To(.+)$/);
  if (!m) return null;
  return { pickupCity: m[1], dropoffCity: m[2] };
}

export function getDefaultPoint(hubCity) {
  const city = String(hubCity || '');
  const station = MAIN_STATION_BY_CITY[city];
  const coords = MAIN_STATION_COORDS[city];
  const label = station ? `${station}, ${city}` : city;
  return {
    type: 'main_station',
    label,
    placeId: null,
    lat: coords?.lat,
    lng: coords?.lng,
  };
}

export function formatParticipantPoint(point, hubCity, placeName = null) {
  if (typeof point === 'string' && point.trim()) {
    return placeName ? composePlaceLabel(placeName, point) : point;
  }
  if (!point) {
    if (hubCity) {
      const station = MAIN_STATION_BY_CITY[hubCity];
      return station ? `${station}, ${hubCity}` : hubCity;
    }
    return '—';
  }
  if (point.label) {
    return point.placeName
      ? composePlaceLabel(point.placeName, point.label)
      : point.label;
  }
  if (point.type === 'main_station' && hubCity) {
    const station = MAIN_STATION_BY_CITY[hubCity];
    return station ? `${station}, ${hubCity}` : hubCity;
  }
  return 'Custom address';
}

/** Display stored participant point label with optional provider place name. */
export function formatStoredParticipantPoint(label, hubCity, placeName = null) {
  if (label) {
    return placeName ? composePlaceLabel(placeName, label) : label;
  }
  return formatParticipantPoint(null, hubCity);
}

/** Map picker value → participant DB columns on insert/update. */
export function participantPointColumns(pickupPoint, dropoffPoint) {
  return {
    pickup_point_type: pickupPoint?.type || 'main_station',
    pickup_point_label: pickupPoint?.label || null,
    pickup_place_id: pickupPoint?.placeId || null,
    pickup_lat: pickupPoint?.lat ?? null,
    pickup_lng: pickupPoint?.lng ?? null,
    dropoff_point_type: dropoffPoint?.type || 'main_station',
    dropoff_point_label: dropoffPoint?.label || null,
    dropoff_place_id: dropoffPoint?.placeId || null,
    dropoff_lat: dropoffPoint?.lat ?? null,
    dropoff_lng: dropoffPoint?.lng ?? null,
  };
}

export function participantRowToPickupPoint(row) {
  if (!row) return null;
  return {
    type: row.pickup_point_type || 'main_station',
    label: row.pickup_point_label || formatParticipantPoint(null, row.pickup_location),
    placeName: row.pickup_point_place_name || null,
    placeId: row.pickup_place_id || null,
    lat: row.pickup_lat ?? undefined,
    lng: row.pickup_lng ?? undefined,
  };
}

export function participantRowToDropoffPoint(row) {
  if (!row) return null;
  return {
    type: row.dropoff_point_type || 'main_station',
    label: row.dropoff_point_label || formatParticipantPoint(null, row.dropoff_location),
    placeName: row.dropoff_point_place_name || null,
    placeId: row.dropoff_place_id || null,
    lat: row.dropoff_lat ?? undefined,
    lng: row.dropoff_lng ?? undefined,
  };
}
